import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { QuestionService, QuestionVersion, QuestionFilters } from '../../services/QuestionService';
import { QuestionStatus, Category } from '../../types/enums';
import FilterForm from '../FilterForm';

interface SortConfig { key: string; direction: 'asc' | 'desc'; }
interface Question {
  id: number; question: string; level: number; status: string; category: string;
  vector_db_data: string; data_type: string; additional_info?: string;
  created_at: string; updated_at: string; versions: QuestionVersion[];
}
interface QuestionListProps { onRowClick: (question: Question) => void; onAddQuestion: () => void; }
export interface QuestionListRef { fetchData: () => void; }

const glowBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  background: 'linear-gradient(120deg, #0b3d91, #1e68ff)', color: '#fff', border: 'none',
  borderRadius: '12px', padding: '10px 18px', fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 10px 26px rgba(14,52,161,0.35)', transition: 'transform 0.15s ease, box-shadow 0.15s ease',
};

const QuestionList = forwardRef<QuestionListRef, QuestionListProps>(({ onRowClick, onAddQuestion }, ref) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'id', direction: 'asc' });
  const [pagination, setPagination] = useState({ page: 1, size: 10, total: 0, total_pages: 0 });
  const [filters, setFilters] = useState<QuestionFilters>({ level: 0, status: '', category: '', question: '' });

  const fetchData = async () => {
    if (!loading) setTableLoading(true);
    setError(null);
    try {
      const cleanFilters = {
        level: filters.level || undefined,
        status: filters.status || undefined,
        category: filters.category || undefined,
        question: filters.question || undefined,
      };
      const data = await QuestionService.getAllQuestions(cleanFilters, {
        page: pagination.page, size: pagination.size, sort_by: sortConfig.key, sort_order: sortConfig.direction,
      });
      setQuestions(data.items || []);
      setPagination(prev => ({ ...prev, total: data.total || 0, total_pages: data.total_pages !== undefined ? data.total_pages : Math.ceil((data.total || 0) / pagination.size) }));
    } catch (err) {
      setError('Erro ao carregar questões');
      console.error('Error fetching questions:', err);
      setQuestions([]);
    } finally { setLoading(false); setTableLoading(false); }
  };

  useEffect(() => { fetchData(); }, [pagination.page, pagination.size, sortConfig, filters]);
  useImperativeHandle(ref, () => ({ fetchData }));

  const handleSort = (key: string) => {
    const newDirection = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction: newDirection });
    if (pagination.page !== 1) setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handleFilterSubmit = (newFilters: QuestionFilters) => {
    setFilters(newFilters);
    if (pagination.page !== 1) setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handlePageChange = (newPage: number) => { if (newPage >= 1) setPagination(prev => ({ ...prev, page: newPage })); };

  if (loading) return <div className="text-center p-6 text-[#666]">Carregando questões...</div>;
  if (error && questions.length === 0) return <div className="text-center p-6 text-red-600">{error}</div>;

  const thClass = "px-4 py-3 text-left font-semibold text-[#333] bg-[#f8f9fa] border-b-2 border-[#dee2e6] cursor-pointer hover:bg-[#e9ecef] transition-colors";
  const tdClass = "px-4 py-3 border-b border-[#dee2e6] text-[#333]";

  return (
    <div className="p-6 w-full box-border max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 px-2">
        <h2 className="m-0 text-2xl font-bold" style={{ color: '#f6fbff', textShadow: '0 8px 20px rgba(120,190,255,0.28)' }}>
          Lista de Questões
        </h2>
        <button style={glowBtn} onClick={onAddQuestion}>+ Nova Questão</button>
      </div>

      <FilterForm
        onFilterSubmit={handleFilterSubmit}
        initialFilters={filters}
        filterOptions={{
          levels: [{ id: 1, name: 'Nível 1' }, { id: 2, name: 'Nível 2' }, { id: 3, name: 'Nível 3' }],
          statuses: Object.values(QuestionStatus).map(s => ({ id: s, name: s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() })),
          categories: Object.values(Category).map(c => ({ id: c, name: c.charAt(0).toUpperCase() + c.slice(1) })),
        }}
      />

      {error && <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 px-3 py-2 rounded text-sm mb-4">{error}</div>}

      {/* Table */}
      <div className={`bg-white rounded-lg shadow-sm overflow-hidden mb-6 relative ${tableLoading ? 'after:absolute after:inset-0 after:bg-white/70 after:z-10' : ''}`}>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {[
                { key: 'id', label: 'ID' }, { key: 'question', label: 'Pergunta' }, { key: 'level', label: 'Nível' },
                { key: 'status', label: 'Status' }, { key: 'category', label: 'Categoria' }, { key: 'data_type', label: 'Tipo de Dado' },
                { key: 'vector_db_data', label: 'Dados Vector DB' }, { key: 'created_at', label: 'Data de Criação' },
                { key: 'updated_at', label: 'Data de Alteração' }, { key: 'versions', label: 'Versões' },
              ].map(({ key, label }) => (
                <th key={key} className={thClass} onClick={() => key !== 'versions' && handleSort(key)}>
                  {label} {sortConfig.key === key && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {questions.length > 0 ? questions.map(q => (
              <tr key={q.id} className="cursor-pointer hover:bg-[#f8f9fa] transition-colors" onClick={() => onRowClick(q)}>
                <td className={tdClass}>{q.id}</td>
                <td className={tdClass}>{q.question}</td>
                <td className={tdClass}>{q.level}</td>
                <td className={tdClass}>{q.status}</td>
                <td className={tdClass}>{q.category}</td>
                <td className={tdClass}>{q.data_type}</td>
                <td className={tdClass}>
                  <span title={q.vector_db_data}>{q.vector_db_data.length > 20 ? `${q.vector_db_data.substring(0, 20)}...` : q.vector_db_data}</span>
                </td>
                <td className={tdClass + " whitespace-nowrap text-[#666] text-sm"}>{new Date(q.created_at).toLocaleDateString('pt-BR')}</td>
                <td className={tdClass + " whitespace-nowrap text-[#666] text-sm"}>{new Date(q.updated_at).toLocaleDateString('pt-BR')}</td>
                <td className={tdClass}>{q.versions?.length || 0} versões</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={10} className="text-center p-10 text-[#666] italic">Nenhuma questão encontrada.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center gap-2 mt-6">
        <button style={glowBtn} onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1 || tableLoading} className="disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none">Anterior</button>
        <span className="inline-block px-3 py-2 text-[#dfe8ff] text-sm">Página {pagination.page}</span>
        <button style={glowBtn} onClick={() => handlePageChange(pagination.page + 1)} disabled={tableLoading} className="disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none">Próxima</button>
      </div>
    </div>
  );
});

QuestionList.displayName = 'QuestionList';
export default QuestionList;